/**
 * DataProfilerPage — Top-level page composing all 34 modular Data Profiler
 * components into a unified, SSE-driven profiling experience.
 *
 * Replaces the 2,676-line DataProfilingView prototype with the new scalable
 * architecture built across 16 sprints.
 *
 * Component map (by stage):
 *   connect  → EmptySessionState, DB/Upload dialogs
 *   discovery→ ScoreHero, FilterBar, CardGrid, TableCard, SSEProgressCard,
 *              ViewToggleBar, DetailPanel (6 tabs)
 *   validate → NLRuleInput, FormBuilder, RuleReflection,
 *              DeterministicBanner, governance components
 *   share    → ExportMenu, handoff actions
 *
 * Persistent: SessionBreadcrumb, StatusBar, ScreenReaderAnnouncer,
 *             CheckpointBanner (on error)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Hook ────────────────────────────────────────────────────────────────────
import {
  useProfilerSession,
  type SeverityLevel,
} from "./hooks/useProfilerSession";

// ── Discovery components ────────────────────────────────────────────────────
import { SAMPLE_PRESETS, TableSelectStep } from "./components/TableSelectStep";
import { DeepRelationshipOption } from "./components/DeepRelationshipOption";
import { ScoreHero } from "../../components/discovery/ScoreHero";
import { RunOptionsBadges } from "../../components/discovery/RunOptionsBadge";
import { FilterBar } from "../../components/discovery/FilterBar";
import { CardGrid } from "../../components/discovery/CardGrid";
import { TableCard } from "../../components/discovery/TableCard";
import { SSEProgressCard } from "../../components/discovery/SSEProgressCard";

// ── Detail panel ────────────────────────────────────────────────────────────
import {
  DetailPanel,
  type TableDetail,
  type ColumnDetail,
  type IssueDetail,
} from "../../components/detail-panel/DetailPanel";

// ── Navigation ──────────────────────────────────────────────────────────────
import { SessionBreadcrumb } from "../../components/navigation/SessionBreadcrumb";
import { ViewToggleBar } from "../../components/navigation/ViewToggleBar";
import RulesManagementView from "../../components/RulesManagementView";
import {
  FindingsView,
  POSSIBLE_RELATIONSHIP_CATEGORY,
} from "../../components/discovery/FindingsView";
import { RunHistoryView } from "../../components/discovery/RunHistoryView";
import { useSearchParams } from "react-router-dom";

// ── Layout ──────────────────────────────────────────────────────────────────
import { StatusBar } from "../../components/layout/StatusBar";

// ── Feedback ────────────────────────────────────────────────────────────────
import { CheckpointBanner } from "../../components/feedback/CheckpointBanner";
import { ScreenReaderAnnouncer } from "../../components/feedback/ScreenReaderAnnouncer";

// ── States ──────────────────────────────────────────────────────────────────
import { EmptySessionState } from "../../components/states/EmptySessionState";

// ── Beat 3 ──────────────────────────────────────────────────────────────────
import { ExportMenu } from "../../components/beat3/ExportMenu";

// ── Rules ───────────────────────────────────────────────────────────────────
import { NLRuleInput } from "../../components/rules/NLRuleInput";
import {
  FormBuilder,
  type FormBuilderRule,
} from "../../components/rules/FormBuilder";
import {
  RuleReflection,
  type RuleInterpretation,
} from "../../components/rules/RuleReflection";
import { rulesApi, type RuleType, type SchemaColumn } from "../../api/rules";

// ── Data display ────────────────────────────────────────────────────────────
import { type Relationship as MiniRelationship } from "../../components/data-display/RelationshipMiniDiagram";
import {
  ERDiagram,
  relationshipColumns,
} from "../../components/discovery/ERDiagram";
import { SummaryCharts } from "../../components/discovery/SummaryCharts";

// ── Governance ──────────────────────────────────────────────────────────────
import { profilerApi, exportProfileReport } from "../../api/profiler-api";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "../../components/data-source/ConnectDatabaseDialog";
import { DialogOverlay } from "../../components/ui/DialogOverlay";

// ── Icons ───────────────────────────────────────────────────────────────────
import {
  BarChart3,
  ChevronLeft,
  Loader2,
  Plus,
  ShieldAlert,
  Upload,
  X,
} from "lucide-react";

// ── API types ───────────────────────────────────────────────────────────────
import type {
  TableProfile,
  DQIssue,
  DiscoveredFile,
} from "../../api/profiler-api";

// ── Utilities ───────────────────────────────────────────────────────────────
import { cn, openAgentInNewTab } from "../../lib/utils";
import { useRulesGates } from "../../hooks/useRulesGates";

// ═══════════════════════════════════════════════════════════════════════════
// Props
// ═══════════════════════════════════════════════════════════════════════════

interface DataProfilerPageProps {
  onBack: () => void;
  projectId?: string;
  resumeSessionId?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Type mappers — API types → component prop types
// ═══════════════════════════════════════════════════════════════════════════

function toTableDetail(
  table: TableProfile,
  relationships: any | null | undefined,
  geSuite: any | null | undefined,
): TableDetail {
  const name = table.table_name;
  const allRels = (relationships?.relationships ?? []) as any[];
  const pkForTable = (relationships?.pk_candidates?.[name] ?? []) as any[];
  const fkForTable = (relationships?.fk_candidates?.[name] ?? []) as any[];
  const tableRelations = allRels
    .filter((r) => r.source_table === name || r.target_table === name)
    .map((r) => ({
      source_column: relationshipColumns(
        r,
        r.source_table === name ? "source" : "target",
      ),
      target_table: r.source_table === name ? r.target_table : r.source_table,
      target_column: relationshipColumns(
        r,
        r.source_table === name ? "target" : "source",
      ),
      cardinality: r.estimated_cardinality ?? r.cardinality,
      confidence: r.confidence ?? 0,
    }));
  const expectations = Array.isArray(geSuite?.expectations)
    ? geSuite.expectations
        .filter((e: any) => !e.kwargs?.table || e.kwargs.table === name)
        .map((e: any) => ({
          expectation_type: e.expectation_type || "expectation",
          column: e.kwargs?.column,
          details: e.meta?.description,
        }))
    : [];
  return {
    tableName: name,
    score: table.score,
    severity: table.severity,
    columnCount: table.column_count,
    rowCount: table.row_count,
    sampleRowCount: table.row_count_sample,
    sampleCap: table.sample_capped,
    columns: table.columns.map<ColumnDetail>((c) => ({
      name: c.name,
      dtype: c.dtype,
      nullRate: c.null_pct,
      isPII: c.is_pii,
      isPK: c.is_pk,
      isFK: c.is_fk,
    })),
    issues: (table.issues ?? []).map<IssueDetail>((i) => ({
      column: i?.affected_columns?.[0] || "",
      message: i?.description ?? "",
      severity: toUiSeverity(i?.severity) as IssueDetail["severity"],
      dimension: i?.category,
      sampleFailures: Array.isArray(i?.sample_failures)
        ? i.sample_failures
        : undefined,
      sampleFailureColumns: Array.isArray(i?.sample_failure_columns)
        ? i.sample_failure_columns
        : undefined,
    })),
    qualityDimensions: table.quality_dimensions,
    // The PK/FK detector scores candidates on a 0-1 scale and writes the
    // value under `score` (not `confidence`). The UI was reading
    // `pk.confidence`, which doesn't exist, so the ConfidenceBar rendered
    // 0% for every candidate (#7: "what do %ages in primary key
    // candidates indicate?"). Prefer score → confidence → 0.
    pkCandidates: pkForTable.map((pk: any) => ({
      columns: pk.columns ?? pk.source_columns ?? [],
      confidence: pk.score ?? pk.confidence ?? 0,
      evidence: pk.evidence,
      type: pk.type,
    })),
    fkCandidates: fkForTable.map((fk: any) => ({
      columns: fk.columns ?? fk.source_columns ?? [],
      target_table: fk.target_table,
      target_columns: fk.target_columns,
      confidence: fk.score ?? fk.confidence ?? 0,
      evidence: fk.evidence,
    })),
    relations: tableRelations,
    geExpectations: expectations,
  };
}

// Normalise backend severity vocabulary (low/medium/high/critical) to the
// UI's SeverityLevel set (excellent/good/warning/critical). Unknown values
// default to 'warning' to preserve issue visibility without crashing the
// SEVERITY_BADGE lookup in TableCard.
function toUiSeverity(raw: unknown): SeverityLevel {
  const s = String(raw ?? "").toLowerCase();
  if (
    s === "excellent" ||
    s === "good" ||
    s === "moderate" ||
    s === "poor" ||
    s === "warning" ||
    s === "critical"
  ) {
    return s as SeverityLevel;
  }
  if (s === "low") return "good";
  if (s === "medium") return "moderate";
  if (s === "high") return "poor";
  return "moderate";
}

export function toTableIssues(
  issues: DQIssue[] | undefined,
): Array<{ message: string; severity: SeverityLevel }> {
  // Three earlier bugs in one place:
  //
  //   1. The slice(0, 3) here capped the array TableCard receives, so
  //      the "N issues" badge could never read higher than 3 even when
  //      the table had 13 findings. TableCard's body already slices to
  //      3 for the preview and renders a "+N more" hint, so the cap
  //      belongs there, not here.
  //
  //   2. The session-level dq_issues list includes severity="good"
  //      and severity="excellent" entries that record *passing* DQ
  //      checks (e.g. "PK column has 100% uniqueness"). Useful as
  //      evidence in the DQ tab, but they shouldn't inflate the
  //      "issues" badge on the Tables tab — clients reasonably
  //      interpret that count as "real problems".
  //
  //   3. That filter ran on the MAPPED UI severity, and toUiSeverity maps a
  //      real "low" finding to "good" — so low-severity findings vanished
  //      from the badge while the table's score (which counts every
  //      finding's failing rows whatever its severity) still included them:
  //      a table could read 0 / critical with "0 issues". Filter on the RAW
  //      severity instead, so only the passing-check records are dropped.
  const PASSING_CHECK_SEVERITIES = new Set(["good", "excellent"]);
  return (issues ?? [])
    .filter(
      (i) =>
        !PASSING_CHECK_SEVERITIES.has(String(i?.severity ?? "").toLowerCase()),
    )
    .map((i) => ({
      message: i?.description ?? "",
      severity: toUiSeverity(i?.severity),
    }));
}

/**
 * highlightMetrics — split a finding description on numeric tokens and
 * wrap them in a bold + accent-colored span so they stand out in the
 * Summary tab's Top Issues tables.
 *
 * Matches the shapes the profiler + business-rule pass produce:
 *   • Percentages: "38.32%", "100.0%"
 *   • "X of Y rows" pairs: "20,142 of 1,825,118 rows"
 *   • "N row(s) violate": "60,905 row(s) violate"
 *   • Distinct counts: "2 distinct values"
 *   • Bare grouped numbers: "1,825,118"
 *
 * Regex captures BOTH the number and its immediate unit (% / rows / etc.)
 * so the highlighted token reads naturally in-flow.
 */
const _METRIC_RE = new RegExp(
  [
    // "20,142 of 1,825,118 rows" — pair capture
    "\\b\\d[\\d,]*\\s+of\\s+\\d[\\d,]*\\s*(?:rows?|records?)?",
    // "38.32%" / "100.0%"
    "\\d+(?:\\.\\d+)?\\s*%",
    // "N row(s) violate rule" / "N row(s)" / "N rows"
    "\\d[\\d,]*\\s*(?:row\\(s\\)|rows?|records?)",
    // "N distinct values"
    "\\d[\\d,]*\\s*distinct\\s+values?",
    // bare grouped-digit numbers with thousands separators
    "\\b\\d{1,3}(?:,\\d{3})+\\b",
  ].join("|"),
  "gi",
);

function highlightMetrics(text: string): React.ReactNode[] {
  if (!text) return [];
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(_METRIC_RE.source, _METRIC_RE.flags);
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={`${m.index}`}
        className="font-semibold text-amber-300 tabular-nums"
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : [text];
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

export default function DataProfilerPage({
  onBack,
  projectId,
  resumeSessionId,
}: DataProfilerPageProps) {
  const session = useProfilerSession({ projectId, resumeSessionId });
  // rules:execute (describe a rule in English) / rules:create (save one): the
  // DQ Rules panel explains instead of returning a bare 403 (#847).
  const rulesGates = useRulesGates(projectId);

  // Primary scroll container + export section — used to jump the user to the
  // right place when a table detail panel opens or the Export view is entered.
  const primaryScrollRef = useRef<HTMLDivElement>(null);
  const exportSectionRef = useRef<HTMLDivElement>(null);

  // When a table detail panel opens (from any list — the Tables grid OR the
  // "Tables Needing Attention" summary deep down the page), the panel renders
  // at the top. Scroll back to the top so it's immediately visible instead of
  // the user hunting for it. Scroll BOTH the window and the primary container
  // since either can be the scroll context depending on layout.
  useEffect(() => {
    if (!session.selectedTable) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    primaryScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [session.selectedTable]);

  // When the user clicks "Export" (→ Share & Act stage), scroll the export
  // options into view so they land on the actions rather than searching for
  // what changed. Deferred a tick so the share view has rendered.
  useEffect(() => {
    if (session.stage !== "share") return;
    const t = setTimeout(() => {
      exportSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
    return () => clearTimeout(t);
  }, [session.stage]);

  // ── LLM business-insights synthesis (admin-gated, optional) ────────
  // The `profiler_llm_insights` feature flag is admin-controlled; the
  // button below is HIDDEN entirely unless it's enabled, so Tier 3
  // sessions never see an option that could send anything to an LLM.
  // Fetched once at mount — flag flips during a session are rare and
  // a page reload picks them up.
  const [llmInsightsEnabled, setLlmInsightsEnabled] = useState(false);
  const [llmInsightsLoading, setLlmInsightsLoading] = useState(false);
  const [llmInsightsBanner, setLlmInsightsBanner] = useState<{
    kind: "ok" | "warn" | "error";
    text: string;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    profilerApi.getFeatureFlag("profiler_llm_insights").then((enabled) => {
      if (!cancelled) setLlmInsightsEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGenerateLlmInsights = useCallback(async () => {
    if (!session.sessionId) return;
    setLlmInsightsLoading(true);
    setLlmInsightsBanner(null);
    try {
      const resp = (await profilerApi.generateLlmInsights(
        session.sessionId,
      )) as { data?: { insights?: unknown[]; status?: string } };
      const data = resp.data || {};
      const count = Array.isArray(data.insights) ? data.insights.length : 0;
      // Refresh dqAssessment so the new findings show in the Summary
      // panel + Findings tab. The backend has already appended them.
      await session.refreshResults(session.sessionId);
      setLlmInsightsBanner(
        count > 0
          ? { kind: "ok", text: `Added ${count} AI-generated insight(s).` }
          : {
              kind: "warn",
              text:
                data.status === "no_insights"
                  ? "LLM returned no additional insights."
                  : "AI insights complete.",
            },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLlmInsightsBanner({ kind: "error", text: msg });
    } finally {
      setLlmInsightsLoading(false);
    }
  }, [session]);

  // Honour a `?tab=rules|findings|summary|...` deep link (used when /rules
  // redirects here after consolidation). Applied once per mount.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    const valid = [
      "tables",
      "relationships",
      "summary",
      "rules",
      "findings",
      "history",
    ] as const;
    if ((valid as readonly string[]).includes(tab)) {
      session.setActiveView(tab as (typeof valid)[number]);
    }
  }, []);

  // Days 9-10: prefill template passed from Findings "Promote to rule".
  const [promoteDraft, setPromoteDraft] = useState<{
    rule_type:
      | "conditional"
      | "range_domain"
      | "referential_integrity"
      | "temporal_consistency"
      | "cross_table_aggregate"
      | "format_pattern"
      | "statistical_distribution";
    label: string;
    description: string;
    expression: Record<string, unknown>;
    condition_sql?: string;
  } | null>(null);

  // ── Local UI state (dialogs, forms) ─────────────────────────────────────
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Rule reflection state
  const [ruleInterpretation, setRuleInterpretation] =
    useState<RuleInterpretation | null>(null);
  const [ruleReflectionState, setRuleReflectionState] = useState<
    "reflecting" | "displayed" | "editing" | "confirmed" | "failed"
  >("reflecting");
  const [ruleFailureMessage, setRuleFailureMessage] = useState<
    string | undefined
  >(undefined);
  const [ruleSuggestions, setRuleSuggestions] = useState<string[]>([]);
  // Carries what handleNLRuleSubmit parsed but RuleInterpretation's display
  // shape has no room for (the raw structured_expression + description) —
  // needed by onConfirm below to actually persist the rule.
  const [nlRuleDraft, setNlRuleDraft] = useState<{
    ruleType: RuleType;
    expression: Record<string, unknown>;
    description: string;
    nlText: string;
  } | null>(null);
  // NLRuleInput manages its own submit-button state internally and is never
  // told when the parent's async onSubmit resolves — left uncontrolled, its
  // button gets stuck on "Interpreting…" forever once wired to a real
  // backend call that can actually take time or fail (the previous fake
  // setTimeout stub always "succeeded" quickly, so this was never visible).
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [formBuilderError, setFormBuilderError] = useState<string | null>(null);
  const [formBuilderSavedAt, setFormBuilderSavedAt] = useState<number | null>(
    null,
  );

  const VALID_RULE_TYPES: RuleType[] = [
    "conditional",
    "range_domain",
    "referential_integrity",
    "temporal_consistency",
    "cross_table_aggregate",
    "format_pattern",
    "statistical_distribution",
  ];

  // Schema-aware "Try an example" scaffolds for the Add-Rule panel. Built
  // from real table/column names + types/flags already profiled for this
  // engagement (schema metadata only — never sample values), so the
  // examples feel grounded instead of generic. Falls back to NLRuleInput's
  // domain-agnostic defaults when no schema is profiled yet.
  const ruleExamples = useMemo(() => {
    const tables = session.results?.tables ?? [];
    const examples: string[] = [];

    const dateCol = tables
      .flatMap((t) => t.columns.map((c) => ({ table: t.table_name, col: c })))
      .find(({ col }) => /date|timestamp|_dt$|_at$/i.test(col.dtype));
    if (dateCol) {
      examples.push(
        `${dateCol.col.name} in ${dateCol.table} should never be in the future`,
      );
    }

    const keyCol = tables
      .flatMap((t) => t.columns.map((c) => ({ table: t.table_name, col: c })))
      .find(({ col }) => col.is_pk || col.is_fk);
    if (keyCol) {
      examples.push(
        keyCol.col.is_pk
          ? `${keyCol.col.name} in ${keyCol.table} should always be unique`
          : `Every ${keyCol.col.name} in ${keyCol.table} should reference a valid record`,
      );
    }

    const incompleteCol = tables
      .flatMap((t) => t.columns.map((c) => ({ table: t.table_name, col: c })))
      .find(({ col }) => col.null_pct > 0 && !col.is_pii);
    if (incompleteCol) {
      examples.push(
        `${incompleteCol.col.name} in ${incompleteCol.table} should not contain nulls`,
      );
    }

    return examples.length > 0 ? examples : undefined;
  }, [session.results]);

  // Same idea as `ruleExamples` above, but for the Rules tab's "New rule"
  // form (RuleCreateForm), which has a single Describe field + a Label
  // field rather than a rotating example list.
  const ruleFormExample = useMemo(() => {
    const tables = session.results?.tables ?? [];
    const first = tables.find((t) => t.columns.some((c) => !c.is_pii));
    if (!first) return undefined;
    const col = first.columns.find((c) => !c.is_pii) ?? first.columns[0];
    if (!col) return undefined;

    const violationSql = `SELECT * FROM {schema}."${first.table_name}" WHERE "${col.name}" IS NULL`;

    // Structured JSON needs a second, distinct real column to fill out a
    // valid conditional (when/then) shape — prefer a non-numeric column for
    // the "when" side and a numeric one for the "then" side so the example
    // reads sensibly. Falls back to no override (generic SAMPLE_DQ6 stays)
    // when the table doesn't have two suitable distinct columns.
    const isNumeric = (dtype: string) =>
      /int|float|double|decimal|numeric|real|money/i.test(dtype);
    const condCol =
      first.columns.find(
        (c) => !c.is_pii && c.name !== col.name && !isNumeric(c.dtype),
      ) ?? first.columns.find((c) => !c.is_pii && c.name !== col.name);
    const numCol = first.columns.find(
      (c) => !c.is_pii && c.name !== condCol?.name && isNumeric(c.dtype),
    );
    const structuredExpression =
      condCol && numCol
        ? JSON.stringify(
            {
              rule_type: "conditional",
              when_conditions: [
                {
                  column: { table: first.table_name, column: condCol.name },
                  operator: "is_not_null",
                },
              ],
              then_conditions: [
                {
                  column: { table: first.table_name, column: numCol.name },
                  operator: "gte",
                  value: 0,
                },
              ],
            },
            null,
            2,
          )
        : undefined;

    return {
      describe: `${col.name} in ${first.table_name} should not contain nulls`,
      label: `${first.table_name} DQ: ${col.name} check`,
      violationSql,
      structuredExpression,
    };
  }, [session.results]);

  // ── Handlers ────────────────────────────────────────────────────────────

  // Shared by file-select, folder-select, and drag-drop so all three routes
  // dispatch the same way — a single loose file uploads directly, an
  // uploaded .zip (or >1 file, zipped client-side by uploadFiles) goes
  // through the pre-flight review flow.
  const dispatchSelectedFiles = useCallback(
    async (fileList: File[]) => {
      const zipFile = fileList.find((f) => f.name.endsWith(".zip"));
      if (zipFile) {
        await session.uploadFolder(zipFile);
      } else if (fileList.length === 1) {
        await session.uploadFile(fileList[0]);
      } else {
        await session.uploadFiles(fileList);
      }
    },
    [session],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      setShowUploadDialog(false);
      await dispatchSelectedFiles(Array.from(files));
      e.target.value = "";
    },
    [dispatchSelectedFiles],
  );

  const handleFolderSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      setShowUploadDialog(false);
      await dispatchSelectedFiles(Array.from(files));
      e.target.value = "";
    },
    [dispatchSelectedFiles],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (!files || files.length === 0) return;
      setShowUploadDialog(false);
      await dispatchSelectedFiles(Array.from(files));
    },
    [dispatchSelectedFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Connect only — discovery transitions to the `table_select` phase, which
  // renders <TableSelectStep />. The user picks tables + run config, then
  // clicks "Start Profile" which fires off /select-tables and /run via
  // confirmTableSelection().
  const handleConnectExisting = useCallback(
    async (connectionId: string): Promise<ConnectResult> => {
      const sessionId = await session.connectDatabase({
        connection_id: connectionId,
        project_id: projectId,
      });
      return sessionId
        ? { ok: true }
        : { ok: false, error: "Couldn't connect using that saved connection." };
    },
    [session, projectId],
  );

  const handleConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      const sessionId = await session.connectDatabase({
        ...values,
        port: parseInt(values.port, 10),
        project_id: projectId,
      });
      return sessionId
        ? { ok: true }
        : { ok: false, error: "Connection failed." };
    },
    [session, projectId],
  );

  // Schema metadata only (names/types/flags) — never sample values, per the
  // LLM-safety boundary (see CLAUDE.md's sanitize-before-LLM invariant;
  // parseNL sends this schema_context to an LLM backend). Shared by the
  // Summary panel's NL submit below and passed down to the Rules tab's New
  // Rule form so its "Describe in English" parse also resolves against the
  // real profiled schema instead of a hardcoded one.
  const ruleSchemaContext = useMemo(() => {
    const tables: Record<string, SchemaColumn[]> = {};
    for (const t of session.results?.tables ?? []) {
      tables[t.table_name] = t.columns.map((c) => ({
        name: c.name,
        dtype: c.dtype,
        nullable: c.null_pct > 0,
        is_pk: c.is_pk,
        is_fk: c.is_fk,
      }));
    }
    return { tables };
  }, [session.results]);

  const handleNLRuleSubmit = useCallback(
    async (ruleText: string) => {
      setRuleReflectionState("reflecting");
      setRuleFailureMessage(undefined);
      setRuleSuggestions([]);
      setNlSubmitting(true);
      try {
        const res = await rulesApi.parseNL({
          nl_text: ruleText,
          schema_context: ruleSchemaContext,
          project_id: projectId,
        });
        if (!res.success) {
          setRuleReflectionState("failed");
          setRuleFailureMessage(
            res.error_message || "Could not interpret this rule.",
          );
          setRuleSuggestions(res.suggestions || []);
          return;
        }
        // The expression's own rule_type: the label ("Range / Domain") does not map back by lower-casing.
        const canonical = String(
          (res.expression?.rule_type as string | undefined) ||
            res.rule_type_label ||
            "",
        )
          .toLowerCase()
          .replace(/ /g, "_");
        const ruleType: RuleType = VALID_RULE_TYPES.includes(
          canonical as RuleType,
        )
          ? (canonical as RuleType)
          : "conditional";
        const firstBinding = res.column_bindings?.[0];
        setRuleInterpretation({
          ruleType,
          sourceTable:
            firstBinding?.resolved_table ||
            session.filteredTables[0]?.table_name ||
            "unknown",
          joinKeys: [],
          conditions: [],
          columnMappings: (res.column_bindings || []).map((b) => ({
            source: b.nl_reference,
            target: `${b.resolved_table}.${b.resolved_column}`,
            confidence: b.confidence,
          })),
        });
        setNlRuleDraft({
          ruleType,
          expression: res.expression || {},
          description: res.description || ruleText,
          nlText: ruleText,
        });
        setRuleReflectionState("displayed");
      } catch (err) {
        setRuleReflectionState("failed");
        setRuleFailureMessage(err instanceof Error ? err.message : String(err));
      } finally {
        setNlSubmitting(false);
      }
    },
    [session.results, session.filteredTables, projectId],
  );

  // Rule types this quick form can express precisely with the DSL
  // (StructuredRuleExpression, see ztd_app/tools/profiler/rules/expression.py).
  // "unique" and "aggregate"/"conditional" need richer fields (aggregate_fn,
  // when/then condition sets) than this 6-field form captures — rather than
  // guess at a payload the backend executor might silently misinterpret,
  // those are rejected client-side with a pointer to the full Rules tab.
  const FORM_BUILDER_TYPE_MAP: Record<string, RuleType | undefined> = {
    not_null: "range_domain",
    range: "range_domain",
    pattern: "format_pattern",
    referential: "referential_integrity",
  };
  const FORM_BUILDER_OPERATOR_MAP: Record<string, string> = {
    ">": "gt",
    ">=": "gte",
    "<": "lt",
    "<=": "lte",
    "==": "eq",
    "!=": "neq",
    matches: "matches",
    between: "between",
  };

  const handleFormBuilderSubmit = useCallback(
    async (rule: FormBuilderRule) => {
      setFormBuilderError(null);
      setFormBuilderSavedAt(null);
      if (!projectId) {
        setFormBuilderError("No active project — cannot save this rule.");
        return;
      }
      const ruleType = FORM_BUILDER_TYPE_MAP[rule.ruleType];
      if (!ruleType) {
        setFormBuilderError(
          `"${rule.ruleType}" rules need more detail than this quick form captures — use "New Rule" in the Rules tab for this type.`,
        );
        return;
      }
      let structured_expression: Record<string, unknown>;
      if (ruleType === "referential_integrity") {
        structured_expression = {
          rule_type: ruleType,
          source_column: { table: rule.sourceTable, column: rule.sourceColumn },
          target_column: { table: rule.targetTable, column: rule.targetColumn },
          allow_nulls: false,
        };
      } else if (rule.ruleType === "not_null") {
        structured_expression = {
          rule_type: ruleType,
          conditions: [
            {
              column: { table: rule.sourceTable, column: rule.sourceColumn },
              operator: "is_not_null",
            },
          ],
        };
      } else {
        structured_expression = {
          rule_type: ruleType,
          conditions: [
            {
              column: { table: rule.sourceTable, column: rule.sourceColumn },
              operator: FORM_BUILDER_OPERATOR_MAP[rule.operator || ""] || "eq",
              value: rule.value,
            },
          ],
        };
      }
      try {
        await rulesApi.create({
          project_id: projectId,
          label: `${rule.ruleType} check on ${rule.sourceTable}.${rule.sourceColumn}`,
          rule_type: ruleType,
          structured_expression,
          input_mode: "form",
        });
        setFormBuilderSavedAt(Date.now());
      } catch (err) {
        setFormBuilderError(err instanceof Error ? err.message : String(err));
      }
    },
    [projectId],
  );

  // ── Score hero state mapping ────────────────────────────────────────────

  const scoreHeroState = (() => {
    if (session.internalPhase === "empty") return "empty" as const;
    if (session.internalPhase === "profiling") return "streaming" as const;
    if (session.internalPhase === "error") return "error" as const;
    if (session.results) return "complete" as const;
    return "empty" as const;
  })();

  // ── Selected table data ─────────────────────────────────────────────────

  const selectedTableData: TableDetail | null = (() => {
    if (!session.selectedTable || !session.results) return null;
    const table = session.results.tables.find(
      (t) => t.table_name === session.selectedTable,
    );
    if (!table) return null;
    const geForTable =
      session.dqAssessment?.ge_suite_by_table?.[session.selectedTable];
    return toTableDetail(table, session.relationships, geForTable);
  })();

  // ── Disabled views ──────────────────────────────────────────────────────

  const disabledViews: Partial<
    Record<"tables" | "relationships" | "summary", string>
  > = {};
  if (!session.relationships && !session.results)
    disabledViews.relationships = "Complete profiling to view relationships";
  if (!session.results)
    disabledViews.summary = "Complete profiling to view summary";

  // ── Relationships for mini diagram ──────────────────────────────────────

  const getRelationshipsForTable = useCallback(
    (tableName: string): MiniRelationship[] => {
      if (!session.relationships) return [];
      return session.relationships.relationships
        .filter(
          (r) => r.source_table === tableName || r.target_table === tableName,
        )
        .map((r) => ({
          targetTable:
            r.source_table === tableName ? r.target_table : r.source_table,
          sourceColumn: relationshipColumns(
            r,
            r.source_table === tableName ? "source" : "target",
          ),
          targetColumn: relationshipColumns(
            r,
            r.source_table === tableName ? "target" : "source",
          ),
          cardinality: r.cardinality as "1:1" | "1:N" | "N:1" | "N:N",
        }));
    },
    [session.relationships],
  );

  // ── Columns-for-table lookup (for FormBuilder) ──────────────────────────

  const columnsForTable = useCallback(
    (tableName: string): string[] => {
      if (!session.results) return [];
      const table = session.results.tables.find(
        (t) => t.table_name === tableName,
      );
      return table?.columns.map((c) => c.name) || [];
    },
    [session.results],
  );

  // ═══════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="flex flex-col h-full w-full bg-[var(--background,#0a0a14)]">
      {/* ── A11y: Screen reader announcements ─────────────────────── */}
      <ScreenReaderAnnouncer message={session.announcement} />

      {/* ── Secondary malware-scan status banner ────────────────────
          Shown instead of the error banner while start_run's secondary
          (cloud-native) scan gate is pending: the hook is auto-polling and
          will resume profiling on its own once it clears, so this is a
          status, not something the user needs to act on — no Retry button
          to click (there was never anything for it to correctly do; see
          pollSecondaryScanCleared). */}
      {session.scanPending && (
        <CheckpointBanner
          type="hitl_gate"
          message={
            session.scanMessage ||
            "Scanning this file for malware before profiling begins. This can take anywhere from a few seconds to a few minutes — profiling will start automatically once it clears."
          }
          primaryAction={{ label: "Start Over", onClick: session.clearSession }}
        />
      )}

      {/* ── Error banner ──────────────────────────────────────────── */}
      {session.error && (
        <CheckpointBanner
          type="processing_error"
          message={session.error}
          primaryAction={{
            label: session.internalPhase === "error" ? "Start Over" : "Dismiss",
            onClick:
              session.internalPhase === "error"
                ? session.clearSession
                : () => {},
          }}
          secondaryAction={
            session.sessionId && session.errorRetryable
              ? {
                  label: "Retry",
                  // Never pass session.startProfiling directly as a DOM
                  // onClick — CheckpointBanner wires this to a native
                  // <button onClick>, which invokes it with the click's
                  // SyntheticEvent as the first argument. startProfiling's
                  // first argument is an optional explicit session id, so
                  // that event object would be sent as the session id
                  // (see startProfiling's explicitSessionId param).
                  onClick: () => {
                    void session.startProfiling();
                  },
                }
              : undefined
          }
        />
      )}

      {/* ── Session breadcrumb ────────────────────────────────────── */}
      <div className="border-b border-[var(--border,rgba(255,255,255,0.06))]">
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors py-2"
              aria-label="Go back"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div className="w-px h-4 bg-[var(--border)]" />
            <SessionBreadcrumb
              currentStage={session.stage}
              completedStages={session.completedStages}
              dataDepth={
                session.selectedTable
                  ? { table: session.selectedTable }
                  : undefined
              }
              onStageClick={(stage) => session.setStage(stage)}
              // "Validate" has no screen yet — hide it until it's built so the
              // breadcrumb only shows reachable steps (Connect → Discovery →
              // Share & Act).
              hideStages={["validate"]}
            />
          </div>

          {/* Action buttons (top-right) */}
          <div className="flex items-center gap-2">
            {session.results && (
              <>
                <button
                  onClick={() => setShowRulesPanel(!showRulesPanel)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 text-[var(--foreground)] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Rule
                </button>
                <button
                  onClick={() => session.setStage("share")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-[var(--primary)]/10 hover:bg-[var(--primary)]/20 text-[var(--primary)] transition-colors"
                >
                  Export
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content area ─────────────────────────────────────── */}
      {/* April 2026: explicit min-w-0 added so flex-1 can grow into the
          full available width without being held back by content's
          intrinsic min-content size. Without this, the CardGrid was
          collapsing to ~656px on some layouts. */}
      <div className="flex-1 overflow-hidden flex min-w-0 w-full">
        {/* ── Primary panel ────────────────────────────────────────── */}
        <div
          ref={primaryScrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar min-w-0"
        >
          {/* ──────────────────────────────────────────────────────── */}
          {/* STAGE: CONNECT (empty state) */}
          {/* ──────────────────────────────────────────────────────── */}
          {/* April 2026: when the URL deep-links to ?tab=rules (typical
              on the redirected /agents/rules-engine pathway after the
              merger of the standalone Rules Engine into this agent),
              render the Rules / Library UI directly without requiring
              an active profile session. Rule authoring + library
              browsing are both useful before profiling has run, and
              the prior standalone /agents/rules-engine page worked
              that way. Falls through to the normal empty-state UX
              for any other tab parameter. */}
          {session.internalPhase === "empty" &&
            !session.isLoading &&
            searchParams.get("tab") === "rules" &&
            projectId && (
              <div className="overflow-y-auto h-full custom-scrollbar relative z-10 p-6">
                <RulesManagementView projectId={projectId} onBack={onBack} />
              </div>
            )}
          {/* When deep-linking to ?tab=history (e.g. from the Project
              dashboard "Recent Profile Runs" panel), show run history
              directly even when there is no active session. */}
          {session.internalPhase === "empty" &&
            !session.isLoading &&
            searchParams.get("tab") === "history" && (
              <div className="overflow-y-auto h-full custom-scrollbar relative z-10 p-6">
                <RunHistoryView
                  projectId={projectId || undefined}
                  sessionId={session.sessionId || undefined}
                />
              </div>
            )}
          {session.internalPhase === "empty" &&
            !session.isLoading &&
            searchParams.get("tab") !== "rules" &&
            searchParams.get("tab") !== "history" && (
              <EmptySessionState
                onConnectDatabase={() => setShowConnectDialog(true)}
                onUploadFiles={() => setShowUploadDialog(true)}
              />
            )}

          {/* ──────────────────────────────────────────────────────── */}
          {/* Loading state during upload/connect */}
          {/* ──────────────────────────────────────────────────────── */}
          {session.isLoading && (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
              <Loader2 className="w-10 h-10 text-[var(--primary)] animate-spin mb-4" />
              <p className="text-body text-[var(--muted-foreground)]">
                {session.internalPhase === "uploading"
                  ? "Uploading file…"
                  : session.internalPhase === "pre_flight_processing"
                    ? "Scanning uploaded files…"
                    : "Connecting to database…"}
              </p>
              {/* A large zip/folder transfer is bounded by the browser's
                  own upload bandwidth, not backend processing — a bare
                  spinner with no feedback for several minutes reads as
                  hung. This is the only signal fetch() can't provide
                  (no upload-progress API), hence the XHR-based upload. */}
              {session.internalPhase === "uploading" &&
                session.uploadProgress != null && (
                  <div className="w-64 mt-4">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[var(--primary)] transition-all duration-200"
                        style={{ width: `${session.uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-caption text-[var(--muted-foreground)] mt-1.5">
                      {session.uploadProgress}%
                    </p>
                  </div>
                )}
            </div>
          )}

          {/* ──────────────────────────────────────────────────────── */}
          {/* STAGE: PRE-FLIGHT REVIEW (FR6, FR7 — multi-file) */}
          {/* ──────────────────────────────────────────────────────── */}
          {session.internalPhase === "pre_flight" &&
            session.preFlightReport && (
              <div className="p-6 space-y-6 max-w-4xl mx-auto">
                <div>
                  <h2 className="text-heading-2 text-[var(--foreground)] mb-1">
                    Pre-Flight Review
                  </h2>
                  <p className="text-body text-[var(--muted-foreground)]">
                    {session.preFlightReport.total_files} files discovered in{" "}
                    <code className="text-xs bg-white/5 px-1 rounded">
                      {session.preFlightReport.root_path}
                    </code>
                    . Review and select files to profile.
                  </p>
                </div>

                {/* Summary cards — Encoding Issue intentionally omitted from
                    this grid: it's not a gate on what gets included (unlike
                    the other three, which do exclude files by default), so
                    giving it equal visual weight here overstated it as a
                    blocker. Its count is folded into the narrative banner
                    below instead, where it can carry the actual context. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <PreFlightMetric
                    label="Processable"
                    value={session.preFlightReport.processable_count}
                    color="var(--readiness-excellent)"
                  />
                  <PreFlightMetric
                    label="Unreadable"
                    value={session.preFlightReport.unreadable_count}
                    color="var(--readiness-critical)"
                  />
                  <PreFlightMetric
                    label="Empty"
                    value={session.preFlightReport.empty_count}
                    color="var(--readiness-warning)"
                  />
                  <PreFlightMetric
                    label="Unsupported"
                    value={session.preFlightReport.unsupported_count}
                    color="var(--muted-foreground)"
                  />
                </div>

                {/* Encoding-issue narrative — a low-confidence charset read
                    doesn't mean the file is broken; a short plain-text file
                    is often genuinely indistinguishable from UTF-8 to the
                    detector. These files are still included and profiled
                    with the detector's best guess by default. */}
                {session.preFlightReport.encoding_ambiguous_count > 0 && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                    <p className="text-xs text-blue-300/80">
                      <span className="font-semibold text-blue-300">
                        {session.preFlightReport.encoding_ambiguous_count} file
                        {session.preFlightReport.encoding_ambiguous_count === 1
                          ? ""
                          : "s"}{" "}
                        flagged for encoding confidence:
                      </span>{" "}
                      the detector was less than 80% confident in a file's
                      character encoding — often just a short or plain-text file
                      with nothing distinctive to detect from. These files are
                      still included by default and profiled using the
                      detector's best guess. It's safe to proceed; re-check a
                      file only if its profiled text later looks garbled.
                    </p>
                  </div>
                )}

                {/* File type breakdown */}
                {Object.keys(session.preFlightReport.files_by_type).length >
                  0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(session.preFlightReport.files_by_type).map(
                      ([ext, count]) => (
                        <span
                          key={ext}
                          className="text-xs bg-white/5 border border-[var(--border)] px-2 py-1 rounded-full text-[var(--muted-foreground)]"
                        >
                          {ext}: {count}
                        </span>
                      ),
                    )}
                    <span className="text-xs text-[var(--muted-foreground)] px-2 py-1">
                      Total:{" "}
                      {(session.preFlightReport.total_size_mb ?? 0).toFixed(1)}{" "}
                      MB
                    </span>
                  </div>
                )}

                {/* Warnings — encoding-confidence and schema-reconciliation
                    warnings are filtered out here: encoding already has its
                    own narrative banner above, and reconciliation is only
                    meaningful when a folder holds multiple PARTS of the
                    SAME logical table (e.g. dated exports) — for the common
                    case of one file per distinct table it reports "disjoint
                    schemas" or spurious same-file column-name similarity,
                    which is expected, not actionable, and was pure noise on
                    this screen. Only genuinely actionable warnings (files
                    that got excluded outright) surface here now. */}
                {session.preFlightReport.warnings.filter(
                  (w) =>
                    !w.includes("ambiguous encoding") &&
                    !w.toLowerCase().includes("schema"),
                ).length > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-400 mb-1">
                      Warnings
                    </p>
                    {session.preFlightReport.warnings
                      .filter(
                        (w) =>
                          !w.includes("ambiguous encoding") &&
                          !w.toLowerCase().includes("schema"),
                      )
                      .map((w, i) => (
                        <p key={i} className="text-xs text-amber-300/70">
                          {w}
                        </p>
                      ))}
                  </div>
                )}

                {/* Opt-in value-based relationship suggestions (extracts only). Placed above the file list so
                    it is seen without scrolling past it. */}
                <DeepRelationshipOption
                  checked={session.runConfig.includeDeepRelationships}
                  onChange={(v) =>
                    session.setRunConfig({ includeDeepRelationships: v })
                  }
                />

                {/* File selection table */}
                <div className="glass-surface rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)]">
                    <span className="text-sm text-[var(--foreground)] font-medium">
                      {session.selectedFileIds.size} of{" "}
                      {
                        session.preFlightReport.files.filter(
                          (f) =>
                            f.status === "processable" ||
                            f.status === "encoding_ambiguous",
                        ).length
                      }{" "}
                      files selected
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => session.setAllFilesSelected(true)}
                        className="text-xs text-[var(--primary)] hover:underline"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => session.setAllFilesSelected(false)}
                        className="text-xs text-[var(--muted-foreground)] hover:underline"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {session.preFlightReport.files.map((file) => (
                      <FileRow
                        key={file.file_id}
                        file={file}
                        selected={session.selectedFileIds.has(file.file_id)}
                        onToggle={() =>
                          session.toggleFileSelection(file.file_id)
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Sample size — same per-table row cap DB sources already
                    get via TableSelectStep. DQ rules re-scan the full file
                    regardless of this cap, so a smaller sample doesn't cost
                    rule accuracy, only ingest time. */}
                <div className="glass-surface rounded-xl p-4">
                  <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide mb-2">
                    Sample size cap (per file)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {SAMPLE_PRESETS.map((p) => {
                      const active = session.runConfig.sampleRows === p.value;
                      return (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() =>
                            session.setRunConfig({ sampleRows: p.value })
                          }
                          className={cn(
                            "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                            active
                              ? "bg-[var(--primary)] text-[var(--primary-foreground)] border-[var(--primary)]"
                              : "bg-white/5 text-[var(--muted-foreground)] border-[var(--border)] hover:text-[var(--foreground)]",
                          )}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[var(--muted-foreground)]">
                    {session.runConfig.sampleRows == null
                      ? "Profiling all rows — slow for large files."
                      : `Up to ${session.runConfig.sampleRows.toLocaleString()} rows per file.`}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={session.clearSession}
                    className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={session.confirmFileSelection}
                    disabled={session.selectedFileIds.size === 0}
                    className="px-6 py-2.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Profile {session.selectedFileIds.size} File
                    {session.selectedFileIds.size !== 1 ? "s" : ""}
                  </button>
                </div>
              </div>
            )}

          {/* ──────────────────────────────────────────────────────── */}
          {/* STAGE: TABLE SELECT (DB source — pick tables + run config) */}
          {/* ──────────────────────────────────────────────────────── */}
          {session.internalPhase === "table_select" && (
            <TableSelectStep
              sourceName={session.sourceName || "Source"}
              tables={session.discoveredTables}
              selected={session.selectedTableNames}
              onToggle={session.toggleTableSelection}
              onToggleAll={session.setAllTablesSelected}
              runConfig={session.runConfig}
              onRunConfigChange={session.setRunConfig}
              onConfirm={() => {
                void session.confirmTableSelection();
              }}
              onCancel={session.clearSession}
              isLoading={session.isLoading}
              sourceType={session.sourceType}
              currentSchema={session.currentSchema}
              availableSchemas={session.availableSchemas}
              isSwitchingSchema={session.isSwitchingSchema}
              schemaSwitchError={session.schemaSwitchError}
              onSwitchSchema={session.switchSchema}
            />
          )}

          {/* ──────────────────────────────────────────────────────── */}
          {/* STAGE: DISCOVERY (profiling or results) */}
          {/* ──────────────────────────────────────────────────────── */}
          {(session.internalPhase === "profiling" ||
            session.internalPhase === "loaded") && (
            <div className="p-6 space-y-6">
              {/* Score Hero */}
              <ScoreHero
                title="Overall Data Quality Score"
                score={session.aggregateScore}
                tablesProfiled={session.tablesComplete}
                totalTables={session.tablesTotal}
                state={scoreHeroState}
              />
              {/* What the score is a score of, when rules or keys were switched off for this run. */}
              <RunOptionsBadges
                options={session.results?.run_options}
                className="-mt-3 justify-center"
              />

              {/* View toggle (tables/relationships/summary/rules/findings) */}
              {session.results && (
                <ViewToggleBar
                  activeView={session.activeView}
                  onViewChange={session.setActiveView}
                  disabledViews={disabledViews}
                  counts={{
                    tables: session.results.tables.length,
                    // Suggestions from the opt-in deep relationship discovery are not findings.
                    findings: (session.dqAssessment?.issues || []).filter(
                      (x) =>
                        (x.category || "").toLowerCase() !==
                        POSSIBLE_RELATIONSHIP_CATEGORY,
                    ).length,
                  }}
                />
              )}

              {/* ── Tables View ──────────────────────────────────── */}
              {session.activeView === "tables" && (
                <div className="flex flex-col max-h-[calc(100vh-320px)] min-h-[400px] -mt-2">
                  {/*
                    Split the Tables view into a pinned header (FilterBar)
                    and a scrollable card-grid body. Before, the entire
                    Discovery panel scrolled as one — which meant the
                    user had to scroll up past the hero + tabs + filter
                    to change severity or search, then back down to find
                    the card. Now the filter sits fixed at the top of
                    this region while the grid scrolls independently.
                  */}
                  {session.results && (
                    <div className="shrink-0 pb-3 border-b border-white/5">
                      <FilterBar
                        severityCounts={session.severityCounts}
                        activeSeverities={session.activeSeverities}
                        onSeverityToggle={session.toggleSeverity}
                        searchQuery={session.searchQuery}
                        onSearchChange={session.setSearchQuery}
                        sortField={session.sortField}
                        sortDirection={session.sortDirection}
                        onSortChange={session.setSorting}
                        density={session.density}
                        onDensityChange={session.setDensity}
                        visibleCount={session.filteredTables.length}
                        totalCount={session.results.total_tables}
                      />
                    </div>
                  )}
                  <div
                    className="flex-1 overflow-y-auto custom-scrollbar pt-4"
                    id="table-grid-scroll"
                  >
                    <div className="w-full pb-4 pr-2">
                      {/* Card grid: mix of progress cards and completed cards */}
                      <CardGrid forceCompact={session.density === "list"}>
                        {/* In-progress tables (SSEProgressCard) */}
                        {Object.values(session.tableProgress)
                          .filter((tp) => tp.status === "profiling")
                          .map((tp) => (
                            <SSEProgressCard
                              key={`progress-${tp.tableName}`}
                              tableName={tp.tableName}
                              progress={tp.progress}
                              phase={tp.phase}
                              compact={session.density === "list"}
                            />
                          ))}

                        {/* Error tables (SSEProgressCard with error) */}
                        {Object.values(session.tableProgress)
                          .filter((tp) => tp.status === "error")
                          .map((tp) => (
                            <SSEProgressCard
                              key={`error-${tp.tableName}`}
                              tableName={tp.tableName}
                              progress={tp.progress}
                              phase={tp.phase}
                              error={tp.error}
                              retryable={tp.retryable}
                              compact={session.density === "list"}
                            />
                          ))}

                        {/* Completed tables from results (TableCard) */}
                        {session.filteredTables.map((table) => (
                          <TableCard
                            key={table.table_name}
                            tableName={table.table_name}
                            columnCount={table.column_count}
                            rowCount={table.row_count}
                            sampleRowCount={table.row_count_sample}
                            sampleCap={table.sample_capped}
                            score={table.score}
                            severity={table.severity as SeverityLevel}
                            issues={toTableIssues(table.issues)}
                            state={
                              session.selectedTable === table.table_name
                                ? "selected"
                                : "default"
                            }
                            compact={session.density === "list"}
                            onClick={() =>
                              session.selectTable(table.table_name)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                session.selectTable(table.table_name);
                              }
                            }}
                          />
                        ))}
                      </CardGrid>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Relationships View ───────────────────────────── */}
              {session.activeView === "relationships" &&
                (session.relationships || session.results) && (
                  <div className="space-y-8 max-w-6xl">
                    {/* ER diagram (Mermaid) — visual overview of the inferred
                      relationships. Easier to grok at a glance than the
                      table; the table stays below for precise details. */}
                    {(session.relationships?.relationships.length ?? 0) > 0 && (
                      <section className="space-y-3">
                        <div className="flex items-baseline gap-3">
                          <h3 className="text-lg font-semibold text-[var(--foreground)]">
                            ER Diagram
                          </h3>
                          <span className="text-sm text-[var(--muted-foreground)]">
                            inferred from column names and value overlap
                          </span>
                        </div>
                        <ERDiagram
                          mermaid={
                            (session.relationships as any)?.relationship_mermaid
                          }
                          relationships={
                            session.relationships?.relationships as any
                          }
                        />
                      </section>
                    )}

                    {/* Inferred cross-table relationships — readable table
                      view (source.col → target.col, cardinality, confidence).
                      Keeps precise per-edge detail alongside the diagram. */}
                    <section className="space-y-3">
                      <div className="flex items-baseline gap-3">
                        <h3 className="text-lg font-semibold text-[var(--foreground)]">
                          Inferred Relationships
                        </h3>
                        <span className="text-sm text-[var(--muted-foreground)]">
                          {session.relationships?.relationships.length ?? 0}{" "}
                          detected
                        </span>
                      </div>
                      {(session.relationships?.relationships.length ?? 0) >
                      0 ? (
                        <div className="glass-surface rounded-xl overflow-hidden border border-white/10">
                          <table className="w-full text-sm">
                            <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/60">
                              <tr>
                                <th className="text-left px-4 py-2">Source</th>
                                <th className="text-left px-4 py-2">
                                  → Target
                                </th>
                                <th className="text-left px-4 py-2">Card.</th>
                                <th className="text-right px-4 py-2">
                                  Confidence
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {(session.relationships?.relationships ?? []).map(
                                (r: any, i: number) => (
                                  <tr
                                    key={i}
                                    className="border-t border-white/5 hover:bg-white/5"
                                    // Why the profiler drew it: the name match and the share of values found.
                                    title={
                                      Array.isArray(r.evidence)
                                        ? r.evidence.join("\n")
                                        : r.evidence || undefined
                                    }
                                  >
                                    <td className="px-4 py-2 font-mono text-[13px] text-white/85">
                                      {r.source_table}
                                      <span className="text-white/40">.</span>
                                      <span className="text-[var(--primary)]">
                                        {relationshipColumns(r, "source")}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 font-mono text-[13px] text-white/85">
                                      {r.target_table}
                                      <span className="text-white/40">.</span>
                                      <span className="text-[var(--primary)]">
                                        {relationshipColumns(r, "target")}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-white/70">
                                      {r.estimated_cardinality ||
                                        r.cardinality ||
                                        "N:1"}
                                    </td>
                                    <td className="px-4 py-2 text-right text-white/70">
                                      {Math.round((r.confidence ?? 0) * 100)}%
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="glass-surface rounded-xl p-8 text-center">
                          <p className="text-body text-[var(--muted-foreground)]">
                            No cross-table relationships detected.
                          </p>
                        </div>
                      )}
                    </section>

                    {/* PK Candidates */}
                    {session.relationships?.pk_candidates &&
                      Object.keys(session.relationships.pk_candidates).length >
                        0 && (
                        <div className="space-y-3">
                          <h4 className="text-heading-4 text-[var(--foreground)]">
                            Primary Key Candidates
                          </h4>
                          {Object.entries(
                            session.relationships.pk_candidates,
                          ).map(([tableName, candidates]) => (
                            <div
                              key={tableName}
                              className="glass-surface rounded-xl p-4 space-y-2"
                            >
                              <p className="text-body font-medium text-[var(--foreground)]">
                                {tableName}
                              </p>
                              <div className="space-y-1.5">
                                {candidates.map((pk, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between text-sm"
                                  >
                                    <span className="text-[var(--foreground)] font-mono text-xs bg-white/5 px-2 py-0.5 rounded">
                                      {(
                                        pk.columns ??
                                        (pk as any).source_columns ??
                                        []
                                      ).join(", ")}
                                    </span>
                                    <span className="text-caption text-[var(--muted-foreground)]">
                                      {Math.round(pk.confidence * 100)}%
                                      confidence
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    {/* FK Candidates */}
                    {session.relationships?.fk_candidates &&
                      Object.keys(session.relationships.fk_candidates).length >
                        0 && (
                        <div className="space-y-3">
                          <h4 className="text-heading-4 text-[var(--foreground)]">
                            Foreign Key Candidates
                          </h4>
                          {Object.entries(
                            session.relationships.fk_candidates,
                          ).map(([tableName, candidates]) => (
                            <div
                              key={tableName}
                              className="glass-surface rounded-xl p-4 space-y-2"
                            >
                              <p className="text-body font-medium text-[var(--foreground)]">
                                {tableName}
                              </p>
                              <div className="space-y-1.5">
                                {candidates.map((fk, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center justify-between text-sm"
                                  >
                                    <span className="text-[var(--foreground)]">
                                      <span className="font-mono text-xs bg-white/5 px-2 py-0.5 rounded">
                                        {(
                                          fk.columns ??
                                          (fk as any).source_columns ??
                                          []
                                        ).join(", ")}
                                      </span>
                                      <span className="text-[var(--muted-foreground)] mx-2">
                                        →
                                      </span>
                                      <span className="font-mono text-xs bg-white/5 px-2 py-0.5 rounded">
                                        {fk.target_table}
                                      </span>
                                    </span>
                                    <span className="text-caption text-[var(--muted-foreground)]">
                                      {Math.round(fk.confidence * 100)}%
                                      confidence
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                )}

              {/* ── Summary View ─────────────────────────────────── */}
              {session.activeView === "summary" && session.results && (
                <div className="space-y-6">
                  <h3 className="text-heading-3 text-[var(--foreground)]">
                    Profiling Summary
                  </h3>

                  {/* Aggregate metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard
                      label="Total Tables"
                      value={session.results.total_tables}
                    />
                    {(() => {
                      const actual =
                        session.results.total_rows_actual ??
                        session.results.total_rows;
                      const sampled =
                        session.results.total_rows_sample ??
                        session.results.total_rows;
                      const showSubtitle =
                        sampled > 0 && actual > 0 && sampled < actual;
                      const pct = showSubtitle
                        ? Math.round((sampled / actual) * 1000) / 10
                        : null;
                      return (
                        <MetricCard
                          label="Total Rows"
                          value={actual.toLocaleString()}
                          subtitle={
                            showSubtitle && pct !== null
                              ? `Profiled ${sampled.toLocaleString()} (${pct}%)`
                              : undefined
                          }
                          tip={
                            showSubtitle
                              ? `Total rows in source: ${actual.toLocaleString()}. Profiled ${sampled.toLocaleString()} sampled rows (${pct}%).`
                              : undefined
                          }
                        />
                      );
                    })()}
                    <MetricCard
                      label="Total Columns"
                      value={session.results.total_columns}
                    />
                    <MetricCard
                      label="Profiled At"
                      value={(() => {
                        const raw = session.results.profiled_at;
                        if (!raw) return "just now";
                        const d = new Date(raw);
                        if (isNaN(d.getTime())) return "just now";
                        // Date-only format — cleaner than a long timestamp
                        // that requires a timezone. The full timestamp is
                        // available in the title tooltip for audit use.
                        return d.toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        });
                      })()}
                      tip={(() => {
                        const raw = session.results.profiled_at;
                        if (!raw) return undefined;
                        const d = new Date(raw);
                        return isNaN(d.getTime())
                          ? undefined
                          : `Profiled at ${d.toLocaleString(undefined, {
                              dateStyle: "full",
                              timeStyle: "long",
                            })}`;
                      })()}
                    />
                  </div>

                  {/* Visual strip: severity donut + largest-tables bar +
                      quality-dimension radar. Pulls directly from already-
                      loaded session state so there are no extra fetches. */}
                  <SummaryCharts
                    tables={session.results.tables}
                    dimensionScores={session.dqAssessment?.dimension_scores}
                  />

                  {/* Lowest-scoring tables — replaces the prior Quality
                      Dimensions bar graph (which duplicated the radar in
                      SummaryCharts above). This panel pairs naturally with
                      "Largest Tables by Row Count" — together they answer
                      "where's the volume?" + "where's the pain?", giving a
                      data steward an immediate triage shortlist.
                      April 2026 — user-driven UX consolidation. */}
                  {session.results &&
                    session.results.tables.length > 0 &&
                    (() => {
                      const ranked = [...session.results.tables]
                        .filter((t) => typeof t.score === "number")
                        .sort((a, b) => (a.score || 0) - (b.score || 0))
                        .slice(0, 8);
                      if (ranked.length === 0) return null;
                      const sevColor = (sev: string) => {
                        const s = (sev || "").toLowerCase();
                        if (s === "critical")
                          return "var(--readiness-critical, #ef4444)";
                        if (s === "poor")
                          return "var(--readiness-poor, #f97316)";
                        if (s === "warning" || s === "moderate")
                          return "var(--readiness-moderate, #f59e0b)";
                        if (s === "good")
                          return "var(--readiness-good, #38bdf8)";
                        return "var(--readiness-excellent, #86bc25)";
                      };
                      return (
                        <div className="glass-surface rounded-xl p-6 space-y-3">
                          <div>
                            <h4 className="text-heading-4 text-[var(--foreground)]">
                              Tables Needing Attention
                            </h4>
                            <p className="text-[11px] text-white/55 mt-1">
                              Bottom {ranked.length} tables by score. Click a
                              row to open its detail panel and start triaging.
                            </p>
                          </div>
                          <ul className="space-y-1.5">
                            {ranked.map((t) => {
                              const score = Math.round(t.score || 0);
                              const color = sevColor(t.severity || "");
                              return (
                                <li key={t.table_name}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      session.selectTable(t.table_name)
                                    }
                                    className="w-full text-left group"
                                    title={`Open ${t.table_name} detail panel`}
                                  >
                                    <div className="flex items-baseline justify-between gap-3 mb-1">
                                      <span className="text-[12px] font-mono text-white/85 truncate group-hover:text-white">
                                        {t.table_name}
                                      </span>
                                      <span className="flex items-baseline gap-2 shrink-0">
                                        <span className="text-[11px] uppercase tracking-wider text-white/45">
                                          {t.severity}
                                        </span>
                                        <span
                                          className="text-[13px] font-semibold tabular-nums"
                                          style={{ color }}
                                        >
                                          {score}
                                        </span>
                                      </span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                                      <div
                                        className="h-full rounded-full transition-all group-hover:opacity-80"
                                        style={{
                                          width: `${Math.max(2, Math.min(100, score))}%`,
                                          background: color,
                                        }}
                                      />
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-white/35">
                                      {t.column_count ?? 0} cols ·{" "}
                                      {(t.row_count ?? 0).toLocaleString()} rows
                                      · {t.issues?.length ?? 0} issue
                                      {t.issues?.length === 1 ? "" : "s"}
                                    </div>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })()}

                  {/* Top issues — split by source so users can triage the
                      two classes separately:
                        • Statistical — emitted by the profiler (null rates,
                          cardinality, type/format drift, etc.)
                        • DQ Rules    — emitted by business rules run during
                          the profile pass (library rules with
                          category==='business_rule').
                      Statistical issues are the "what does the data look
                      like" lens; DQ Rules issues are the "what does the
                      business say must be true" lens. */}
                  {session.dqAssessment &&
                    session.dqAssessment.issues.length > 0 &&
                    (() => {
                      const allIssues = session.dqAssessment.issues;
                      const businessRuleIssues = allIssues.filter(
                        (x) =>
                          (x.category || "").toLowerCase() === "business_rule",
                      );
                      // Business-insight findings (deterministic pattern
                      // detectors — sentinels, format variations, etc.)
                      // populate the left panel when present. When the
                      // engagement is brand new and no insight findings
                      // fired yet we fall back to the generic statistical
                      // findings so the panel still has signal.
                      //
                      // Backend marks dedup losers via `suppressed_by`; the
                      // fallback check below counts only LIVE findings so
                      // a (rare) all-suppressed batch still falls through
                      // to statistical rather than showing an empty panel.
                      // Deterministic + LLM business-insight findings
                      // both populate the left panel — both carry the
                      // dq_dimension + severity fields the panel reads.
                      // LLM ones use category="business_insight_llm" so
                      // compliance can grep them as a separate set, but
                      // they render together.
                      const businessInsightIssues = allIssues.filter((x) => {
                        const c = (x.category || "").toLowerCase();
                        return (
                          (c === "business_insight" ||
                            c === "business_insight_llm") &&
                          !(x as any).suppressed_by
                        );
                      });
                      const statisticalIssues = allIssues.filter((x) => {
                        const c = (x.category || "").toLowerCase();
                        return (
                          c !== "business_rule" &&
                          c !== "business_insight" &&
                          c !== "business_insight_llm" &&
                          c !== POSSIBLE_RELATIONSHIP_CATEGORY
                        );
                      });
                      const statsIssues =
                        businessInsightIssues.length > 0
                          ? businessInsightIssues
                          : statisticalIssues;
                      const statsHeading =
                        businessInsightIssues.length > 0
                          ? "Top Business-Context Findings"
                          : "Top Statistical Findings";
                      const statsDescription =
                        businessInsightIssues.length > 0
                          ? "Concrete data issues — sentinel values, format variations, encoding corruption, orphan keys. Deterministic detectors (no LLM)."
                          : "Data-shape anomalies — null rates, cardinality issues, type/format drift. Auto-detected by the profiler.";

                      // Icon + color per severity. Zebra-stripe rows for
                      // scanability (user #4: "add icons, use alternate row
                      // colors / fonts… to make it easier on eyes").
                      const severityConfig: Record<
                        string,
                        { icon: string; cls: string }
                      > = {
                        critical: { icon: "●", cls: "text-red-300" },
                        high: { icon: "●", cls: "text-red-300" },
                        warning: { icon: "▲", cls: "text-amber-300" },
                        medium: { icon: "▲", cls: "text-amber-300" },
                        low: { icon: "✓", cls: "text-sky-300" },
                        good: { icon: "✓", cls: "text-emerald-300" },
                      };
                      const pillCls = (sev: string) => {
                        const s = (sev || "").toLowerCase();
                        if (s === "critical" || s === "high")
                          return "bg-red-400/15 text-red-200 border-red-400/30";
                        if (s === "poor")
                          return "bg-orange-400/15 text-orange-200 border-orange-400/30";
                        if (
                          s === "medium" ||
                          s === "warning" ||
                          s === "moderate"
                        )
                          return "bg-amber-400/15 text-amber-200 border-amber-400/30";
                        if (s === "low")
                          return "bg-sky-400/15 text-sky-200 border-sky-400/30";
                        return "bg-white/5 text-white/65 border-white/10";
                      };

                      const SUMMARY_SEV_ORDER: Record<string, number> = {
                        critical: 0,
                        high: 0,
                        warning: 1,
                        medium: 1,
                        low: 2,
                        good: 3,
                      };

                      const Section = ({
                        heading,
                        headingIcon,
                        description,
                        list,
                        accent,
                      }: {
                        heading: string;
                        headingIcon: React.ReactNode;
                        description: string;
                        list: typeof allIssues;
                        accent: string;
                      }) => {
                        // Backend stamps `priority_score` + `suppressed_by`
                        // on every business_insight finding. Filter out
                        // suppressed (subsumed / per-column-cap losers)
                        // and sort by priority desc — high-impact +
                        // high-severity + high-pct findings rise to the top.
                        // Legacy findings without priority_score (e.g.
                        // statistical fallback) sort below business_insight
                        // by severity only.
                        const sortedList = [...list]
                          .filter((x) => !(x as any).suppressed_by)
                          .sort((a, b) => {
                            const pa = (a as any).priority_score;
                            const pb = (b as any).priority_score;
                            if (
                              typeof pa === "number" &&
                              typeof pb === "number"
                            ) {
                              return pb - pa;
                            }
                            if (typeof pa === "number") return -1;
                            if (typeof pb === "number") return 1;
                            const sevDiff =
                              (SUMMARY_SEV_ORDER[
                                (a.severity || "").toLowerCase()
                              ] ?? 9) -
                              (SUMMARY_SEV_ORDER[
                                (b.severity || "").toLowerCase()
                              ] ?? 9);
                            if (sevDiff !== 0) return sevDiff;
                            // Same-severity tie-break: sort by a stable id so
                            // the visible top-10 doesn't depend on findings'
                            // array arrival order, which is non-deterministic
                            // (business-rule findings execute concurrently on
                            // separate DB connections and are appended in
                            // whichever order their queries complete). Without
                            // this, two profiling runs over identical data can
                            // render a different "Top 10" each time.
                            const ida = String(
                              (a as any).id ?? (a as any).rule_id ?? "",
                            );
                            const idb = String(
                              (b as any).id ?? (b as any).rule_id ?? "",
                            );
                            return ida.localeCompare(idb);
                          });
                        return (
                          <div className="glass-surface rounded-xl overflow-hidden border border-white/10">
                            <div
                              className="px-5 py-3.5 border-b border-white/10"
                              style={{
                                background: `linear-gradient(135deg, ${accent}10, transparent 60%)`,
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span style={{ color: accent }}>
                                  {headingIcon}
                                </span>
                                <h4 className="text-base font-semibold text-white/95">
                                  {heading}
                                </h4>
                                <span
                                  className="ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full"
                                  style={{
                                    background: `${accent}22`,
                                    color: accent,
                                  }}
                                >
                                  {list.length > 10
                                    ? `Top 10 of ${list.length}`
                                    : list.length}
                                </span>
                              </div>
                              <p className="text-[11px] text-white/55 mt-1 leading-snug">
                                {description}
                              </p>
                            </div>
                            {sortedList.length === 0 ? (
                              <p className="text-sm text-white/50 p-5">
                                Nothing to report.
                              </p>
                            ) : (
                              <ul className="divide-y divide-white/5">
                                {sortedList.slice(0, 10).map((issue, idx) => {
                                  const sev = (
                                    issue.severity || "medium"
                                  ).toLowerCase();
                                  const cfg =
                                    severityConfig[sev] ||
                                    severityConfig.medium;
                                  return (
                                    <li
                                      key={issue.id}
                                      className={`flex items-start gap-3 px-5 py-3 ${
                                        idx % 2 === 0 ? "bg-white/[0.015]" : ""
                                      } hover:bg-white/[0.04] transition-colors`}
                                    >
                                      <span
                                        className={`shrink-0 text-lg leading-none mt-0.5 ${cfg.cls}`}
                                        aria-hidden="true"
                                      >
                                        {cfg.icon}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span
                                            className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pillCls(issue.severity || "")}`}
                                          >
                                            {issue.severity || "info"}
                                          </span>
                                          {(issue as any).table && (
                                            <span className="text-[11px] text-white/45 font-mono">
                                              {(issue as any).table}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm text-white/90 leading-snug">
                                          {highlightMetrics(issue.description)}
                                        </p>
                                        {issue.affected_columns &&
                                          issue.affected_columns.length > 0 && (
                                            <p className="text-[11px] text-white/55 mt-1">
                                              <span className="text-white/35">
                                                columns:
                                              </span>{" "}
                                              <span className="font-mono">
                                                {(
                                                  issue.affected_columns ?? []
                                                ).join(", ")}
                                              </span>
                                            </p>
                                          )}
                                        {issue.remediation && (
                                          <p className="text-[11px] text-emerald-300/80 mt-1 italic">
                                            → {issue.remediation}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      };
                      return (
                        <>
                          {/* Admin-gated AI insights toolbar. The button
                              is hidden entirely unless an admin has set
                              feature_flags.profiler_llm_insights=true
                              for this tenant — Tier 3 sessions never
                              see the option. */}
                          {llmInsightsEnabled && (
                            <div className="flex items-center gap-3 mb-3">
                              <button
                                type="button"
                                onClick={handleGenerateLlmInsights}
                                disabled={
                                  llmInsightsLoading || !session.sessionId
                                }
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-400/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Ask an LLM to surface additional business-context findings beyond the deterministic detectors. Sanitised — no raw cell values sent."
                              >
                                {llmInsightsLoading ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <BarChart3 className="w-3.5 h-3.5" />
                                )}
                                Generate AI Insights
                              </button>
                              {llmInsightsBanner && (
                                <span
                                  className={`text-xs ${
                                    llmInsightsBanner.kind === "ok"
                                      ? "text-emerald-300"
                                      : llmInsightsBanner.kind === "warn"
                                        ? "text-amber-300"
                                        : "text-red-300"
                                  }`}
                                >
                                  {llmInsightsBanner.text}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Section
                              heading={statsHeading}
                              headingIcon={<BarChart3 className="w-4 h-4" />}
                              description={statsDescription}
                              list={statsIssues}
                              accent="#f59e0b"
                            />
                            <Section
                              heading="Top DQ Rule Violations"
                              headingIcon={<ShieldAlert className="w-4 h-4" />}
                              description="Approved business rules that failed during this profile run. These reflect codified domain expectations, not inferred anomalies."
                              list={businessRuleIssues}
                              accent="#ef4444"
                            />
                          </div>
                        </>
                      );
                    })()}
                </div>
              )}

              {/* ── Rules View (consolidated DQ Rules Engine) ──── */}
              {session.activeView === "rules" && (
                <div className="-mx-2">
                  <RulesManagementView
                    projectId={projectId || ""}
                    connectionId={session.profiledConnectionId ?? undefined}
                    sourceKind={
                      session.sourceType && session.sourceType !== "database"
                        ? "file"
                        : "database"
                    }
                    onBack={() => session.setActiveView("tables")}
                    embedded
                    prefillDraft={promoteDraft}
                    onPrefillConsumed={() => setPromoteDraft(null)}
                    describeExample={ruleFormExample?.describe}
                    labelExample={ruleFormExample?.label}
                    violationSqlExample={ruleFormExample?.violationSql}
                    structuredExpressionExample={
                      ruleFormExample?.structuredExpression
                    }
                    schemaContext={ruleSchemaContext}
                  />
                </div>
              )}

              {/* ── Findings View (statistical + business-rule) ─ */}
              {session.activeView === "findings" && session.dqAssessment && (
                <FindingsView
                  // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
                  issues={session.dqAssessment.issues ?? []}
                  rules={session.dqAssessment.rules ?? []}
                  projectId={projectId || ""}
                  sessionId={session.sessionId || undefined}
                  sourceName={
                    session.results?.source_name || session.sourceName || ""
                  }
                  onPromoteToRule={(f) => {
                    // Days 9-10: seed the rule form with a VALID rule IR
                    // derived from the finding. Template must pass backend
                    // validation so the user can hit "Create" directly.
                    //
                    // Strategy: every finding maps to a `range_domain` rule
                    // with exactly one condition. `range_domain` is the
                    // simplest rule type that SQL-generates cleanly without
                    // requiring a `then_conditions` companion (the old
                    // `conditional` template produced empty THEN clauses,
                    // which yielded malformed SQL and couldn't be saved).
                    //
                    // The user can edit the operator/value before saving;
                    // the goal is a runnable starting point, not a perfect
                    // business rule.
                    const col = typeof f.column === "string" ? f.column : "";
                    const category = (f.category || "").toLowerCase();

                    // Pick the sharpest single-condition check per category
                    let condition: Record<string, unknown>;
                    let labelSuffix: string;
                    let description: string;
                    if (!col) {
                      // Table-level finding — fall through to a placeholder
                      // that the user can fill in. Use any column from the
                      // table as a stand-in so the rule is syntactically
                      // valid; the author will edit.
                      condition = {
                        column: { table: f.table, column: "id" },
                        operator: "is_not_null",
                      };
                      labelSuffix = (f.category || "issue").replace(/_/g, " ");
                      description = `Auto-drafted from "${f.description}". Update the column + operator to match the business rule.`;
                    } else if (category === "completeness") {
                      condition = {
                        column: { table: f.table, column: col },
                        operator: "is_not_null",
                      };
                      labelSuffix = "must not be null";
                      description = `Auto-drafted from profiling finding "${f.description}". The column should never be NULL — refine to target only business-valid rows if needed.`;
                    } else if (category === "uniqueness") {
                      // Uniqueness can't be expressed as a row-level predicate;
                      // default to a non-null check and leave a hint in the
                      // description so the user knows to switch rule type.
                      condition = {
                        column: { table: f.table, column: col },
                        operator: "is_not_null",
                      };
                      labelSuffix = "uniqueness check";
                      description = `Auto-drafted from profiling finding "${f.description}". For a true uniqueness test, switch to the "Statistical distribution" rule type and add a cardinality_ratio bound.`;
                    } else if (
                      category === "validity" ||
                      category === "consistency"
                    ) {
                      condition = {
                        column: { table: f.table, column: col },
                        operator: "is_not_null",
                      };
                      labelSuffix = `${category} check`;
                      description = `Auto-drafted from profiling finding "${f.description}". Replace the operator with a domain constraint (e.g. in_set, between, matches) that encodes the valid values.`;
                    } else {
                      condition = {
                        column: { table: f.table, column: col },
                        operator: "is_not_null",
                      };
                      labelSuffix = (f.category || "issue").replace(/_/g, " ");
                      description = `Auto-drafted from "${f.description}".`;
                    }

                    // Emit a raw-SQL skeleton (condition_sql) so the promoted
                    // rule runs via the existing SQL evaluator — the
                    // structured-DSL executor isn't built yet. It returns one
                    // row per violation; {schema} is rendered at eval time. This
                    // is a best-effort DRAFT the user refines in the SQL box.
                    const sqlTable = f.table
                      ? `{schema}."${f.table}"`
                      : "{schema}.<table>";
                    const sqlPredicate = col
                      ? `"${col}" IS NULL /* refine: define the violation for ${col} */`
                      : "FALSE /* define the violation condition */";
                    const conditionSql = `SELECT * FROM ${sqlTable} WHERE ${sqlPredicate}`;
                    const template = {
                      rule_type: "range_domain" as const,
                      label: `${[f.table, col].filter(Boolean).join(".") || "New rule"}: ${labelSuffix}`,
                      description,
                      expression: {
                        rule_type: "range_domain",
                        conditions: [condition],
                        allow_nulls: false,
                        case_sensitive: true,
                      },
                      condition_sql: conditionSql,
                      table_name: f.table || undefined,
                    };
                    setPromoteDraft(template);
                    session.setActiveView("rules");
                  }}
                />
              )}

              {/* ── Run History View (FR105-FR109) ─────────────────── */}
              {session.activeView === "history" && (
                <RunHistoryView
                  projectId={projectId || undefined}
                  sessionId={session.sessionId || undefined}
                />
              )}
            </div>
          )}

          {/* ──────────────────────────────────────────────────────── */}
          {/* STAGE: SHARE (export menu) */}
          {/* ──────────────────────────────────────────────────────── */}
          {session.stage === "share" && session.results && (
            <div className="p-6" ref={exportSectionRef}>
              <button
                onClick={() => session.setStage("discovery")}
                className="flex items-center gap-1 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] mb-6 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back to Results
              </button>

              <div className="max-w-lg mx-auto">
                <ExportMenu
                  score={session.aggregateScore}
                  onExportReport={() => {
                    if (!session.sessionId) return;
                    exportProfileReport(session.sessionId, "pdf").catch((e) =>
                      window.alert(`PDF export failed: ${e.message}`),
                    );
                  }}
                  onExportPowerPoint={() => {
                    if (!session.sessionId) return;
                    exportProfileReport(session.sessionId, "pptx").catch((e) =>
                      window.alert(`PowerPoint export failed: ${e.message}`),
                    );
                  }}
                  onSaveRules={() =>
                    window.alert(
                      "Save Rules is being wired to the Rules library (next update).",
                    )
                  }
                  onHandoff={() =>
                    window.alert(
                      "Hand off to the Data Pipeline agent is being built (next update).",
                    )
                  }
                  onExportJSON={() => {
                    const payload = {
                      exported_at: new Date().toISOString(),
                      project_id: projectId,
                      session_id: session.sessionId,
                      source_name: session.results?.source_name,
                      aggregate_score: session.aggregateScore,
                      results: session.results,
                      dq_assessment: session.dqAssessment,
                    };
                    const blob = new Blob([JSON.stringify(payload, null, 2)], {
                      type: "application/json",
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ztd-profile-${session.sessionId || "export"}.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  }}
                  onReProfile={() => {
                    session.clearSession();
                  }}
                  onHandoffGuardian={
                    projectId
                      ? () => {
                          // Reuses the project's already-configured source DB --
                          // DataMaskingView auto-loads it via getProject() on mount.
                          openAgentInNewTab(
                            `/projects/${projectId}/agents/data-guardian`,
                          );
                        }
                      : undefined
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Detail Panel (right slide-over) ─────────────────────── */}
        {session.selectedTable && selectedTableData && (
          <DetailPanel
            table={selectedTableData}
            activeTab={session.activeDetailTab}
            onTabChange={session.setActiveDetailTab}
            onClose={() => session.selectTable(null)}
          />
        )}

        {/* ── Rules Panel (right slide-over, toggled) ─────────────── */}
        {showRulesPanel && session.results && (
          <div className="w-[420px] border-l border-[var(--border)] overflow-y-auto bg-[var(--background)] p-4 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-heading-3 text-[var(--foreground)]">
                DQ Rules
              </h3>
              <button
                onClick={() => setShowRulesPanel(false)}
                className="p-1 rounded hover:bg-white/5"
                aria-label="Close rules panel"
              >
                <X className="w-4 h-4 text-[var(--muted-foreground)]" />
              </button>
            </div>

            {/* Natural language rule input */}
            <NLRuleInput
              onSubmit={handleNLRuleSubmit}
              nlAvailable={true}
              variant="expanded"
              examples={ruleExamples}
              state={nlSubmitting ? "submitting" : undefined}
              blockedReason={rulesGates.nl.reason}
            />

            {/* Rule reflection (if interpreting) */}
            {(ruleInterpretation ||
              ruleReflectionState === "failed" ||
              ruleReflectionState === "confirmed") && (
              <RuleReflection
                interpretation={ruleInterpretation}
                state={ruleReflectionState}
                failureMessage={ruleFailureMessage}
                rephraseSuggestions={ruleSuggestions}
                onRephrase={(suggestion) => handleNLRuleSubmit(suggestion)}
                confirmDisabledReason={rulesGates.create.reason}
                onConfirm={async () => {
                  if (!nlRuleDraft || !projectId) {
                    setRuleReflectionState("failed");
                    setRuleFailureMessage(
                      "Missing project context — cannot save this rule.",
                    );
                    return;
                  }
                  try {
                    await rulesApi.create({
                      project_id: projectId,
                      label: nlRuleDraft.description.slice(0, 120),
                      rule_type: nlRuleDraft.ruleType,
                      structured_expression: nlRuleDraft.expression,
                      input_mode: "natural_language",
                      nl_text: nlRuleDraft.nlText,
                      description: nlRuleDraft.description,
                    });
                    setRuleReflectionState("confirmed");
                    setRuleInterpretation(null);
                    setNlRuleDraft(null);
                    // The "confirmed" card has no exit of its own — without
                    // this, the panel is stuck on "Rule confirmed —
                    // executing…" forever once interpretation is cleared,
                    // since ruleReflectionState never changes again. Show
                    // the success state briefly, then return to idle so the
                    // panel is ready for the next rule.
                    setTimeout(
                      () => setRuleReflectionState("reflecting"),
                      1500,
                    );
                  } catch (err) {
                    setRuleReflectionState("failed");
                    setRuleFailureMessage(
                      err instanceof Error ? err.message : String(err),
                    );
                  }
                }}
                onEdit={() => setRuleReflectionState("editing")}
                onReject={() => {
                  setRuleReflectionState("failed");
                  setRuleInterpretation(null);
                  setNlRuleDraft(null);
                }}
              />
            )}

            {/* Structured form builder */}
            <details className="group">
              <summary className="text-sm text-[var(--muted-foreground)] cursor-pointer hover:text-[var(--foreground)] transition-colors py-2">
                Or build a rule manually…
              </summary>
              <div className="mt-2 space-y-2">
                <FormBuilder
                  tables={session.results.tables.map((t) => t.table_name)}
                  columnsForTable={columnsForTable}
                  onSubmit={handleFormBuilderSubmit}
                  disabledReason={rulesGates.create.reason}
                />
                {formBuilderError && (
                  <p className="text-caption text-[var(--warning)]">
                    {formBuilderError}
                  </p>
                )}
                {formBuilderSavedAt && (
                  <p className="text-caption text-[var(--readiness-excellent)]">
                    Rule saved — find it under "My Rules" in the Rules tab.
                  </p>
                )}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* ── Status bar (footer) ───────────────────────────────────── */}
      <StatusBar
        sseState={session.sseConnectionState}
        onSSEReconnect={session.reconnectSSE}
        checkpointStatus={
          session.internalPhase === "profiling"
            ? `${session.tablesComplete}/${session.tablesTotal} tables`
            : session.results
              ? `Session ${session.sessionId?.slice(0, 8) || ""} — ${session.results.total_tables} tables`
              : undefined
        }
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* DIALOGS */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* ── Upload Dialog ─────────────────────────────────────────── */}
      {showUploadDialog && (
        <DialogOverlay onClose={() => setShowUploadDialog(false)}>
          <div className="glass-surface rounded-2xl p-8 max-w-md w-full mx-4 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-heading-2 text-[var(--foreground)]">
                Upload File
              </h2>
              <button
                onClick={() => setShowUploadDialog(false)}
                className="p-1 rounded hover:bg-white/5"
              >
                <X className="w-5 h-5 text-[var(--muted-foreground)]" />
              </button>
            </div>

            {/* Opt-in value-based relationship suggestions: set BEFORE the file is dropped, since a single file
                starts profiling as soon as it is uploaded. */}
            <DeepRelationshipOption
              checked={session.runConfig.includeDeepRelationships}
              onChange={(v) =>
                session.setRunConfig({ includeDeepRelationships: v })
              }
            />

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-[var(--border)] rounded-xl p-8 text-center cursor-pointer hover:border-[var(--primary)]/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") fileInputRef.current?.click();
              }}
              aria-label="Drop files here or click to browse"
            >
              <Upload className="w-10 h-10 text-[var(--muted-foreground)] mx-auto mb-3" />
              <p className="text-sm text-[var(--foreground)] mb-1">
                Drop files here, or click to browse
              </p>
              <p className="text-xs text-[var(--muted-foreground)]">
                Select one or more files — CSV, Parquet, JSON, Excel (.xlsx),
                TSV
              </p>
              <p className="text-xs text-[var(--muted-foreground)] mt-1">
                Or upload a <strong>.zip</strong> folder archive for multi-file
                profiling with pre-flight review
              </p>
            </div>

            <button
              type="button"
              onClick={() => folderInputRef.current?.click()}
              className="w-full text-sm text-[var(--primary)] hover:underline text-center"
            >
              Or choose an entire folder
            </button>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".csv,.tsv,.parquet,.json,.jsonl,.xlsx,.xls,.zip"
              multiple
              onChange={handleFileSelect}
            />
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              // webkitdirectory is the only cross-browser way to offer a
              // folder picker instead of a file picker — not in the React
              // DOM typings, so it's set as a raw attribute.
              // @ts-expect-error -- webkitdirectory isn't in React's InputHTMLAttributes
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleFolderSelect}
            />
          </div>
        </DialogOverlay>
      )}

      {/* ── Database Connect Dialog (shared across every agent) ──────── */}
      <ConnectDatabaseDialog
        open={showConnectDialog}
        onClose={() => setShowConnectDialog(false)}
        projectId={projectId}
        agentKey="profiler"
        includeFileSources
        onConnectExisting={handleConnectExisting}
        onConnectNew={handleConnectNew}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components (local to this file)
// ═══════════════════════════════════════════════════════════════════════════

/** Simple metric card for summary view */
function MetricCard({
  label,
  value,
  tip,
  subtitle,
}: {
  label: string;
  value: string | number;
  tip?: string;
  subtitle?: string;
}) {
  return (
    <div
      className={`glass-surface rounded-xl p-4 text-center ${tip ? "cursor-help" : ""}`}
      title={tip}
      aria-label={tip ? `${label}: ${value}. ${tip}` : `${label}: ${value}`}
    >
      <p className="text-2xl font-bold text-[var(--foreground)]">{value}</p>
      <p className="text-xs text-[var(--muted-foreground)] mt-1">{label}</p>
      {subtitle && (
        <p className="text-[11px] text-[var(--muted-foreground)] mt-0.5 opacity-80">
          {subtitle}
        </p>
      )}
    </div>
  );
}

/** Severity dot for issue list */
function SeverityDot({ severity }: { severity: SeverityLevel }) {
  const colors: Record<SeverityLevel, string> = {
    excellent: "bg-[var(--readiness-excellent)]",
    good: "bg-[var(--readiness-good)]",
    moderate: "bg-[var(--readiness-moderate)]",
    poor: "bg-[var(--readiness-poor)]",
    warning: "bg-[var(--readiness-warning)]",
    critical: "bg-[var(--readiness-critical)]",
  };
  return (
    <span
      className={cn(
        "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
        colors[severity],
      )}
      aria-label={severity}
    />
  );
}

/** Pre-flight summary metric card */
function PreFlightMetric({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="glass-surface rounded-lg p-3 text-center">
      <p className="text-xl font-bold" style={{ color }}>
        {value}
      </p>
      <p className="text-[11px] text-[var(--muted-foreground)] mt-0.5">
        {label}
      </p>
    </div>
  );
}

/** File row in pre-flight selection table (FR7) */
function FileRow({
  file,
  selected,
  onToggle,
}: {
  file: DiscoveredFile;
  selected: boolean;
  onToggle: () => void;
}) {
  // "encoding_ambiguous" is a soft warning, not a real blocker — the
  // backend's own pre-flight logic treats PROCESSABLE and
  // ENCODING_AMBIGUOUS as the same usable tier (schema reconciliation
  // groups them together; ENCODING_AMBIGUOUS never sets `included =
  // False` the way UNREADABLE/EMPTY/UNSUPPORTED do). A plain-ASCII CSV
  // is often genuinely ambiguous to a charset detector (nothing
  // distinguishes ASCII from UTF-8 when every byte is 7-bit) without
  // anything actually being wrong with the file.
  const isProcessable =
    file.status === "processable" || file.status === "encoding_ambiguous";
  const statusColors: Record<string, string> = {
    processable: "text-green-400",
    unreadable: "text-red-400",
    empty: "text-amber-400",
    encoding_ambiguous: "text-amber-400",
    unsupported: "text-[var(--muted-foreground)]",
  };

  return (
    <label
      className={cn(
        "flex items-center gap-3 px-4 py-2 border-b border-[var(--border)] last:border-0 cursor-pointer transition-colors",
        isProcessable ? "hover:bg-white/3" : "opacity-50 cursor-not-allowed",
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={!isProcessable}
        className="rounded border-[var(--border)] bg-transparent accent-[var(--primary)]"
      />
      <div className="flex-1 min-w-0">
        <p
          className="text-sm text-[var(--foreground)] truncate"
          title={file.relative_path}
        >
          {file.relative_path}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[11px] text-[var(--muted-foreground)]">
            {file.extension}
          </span>
          <span className="text-[11px] text-[var(--muted-foreground)]">
            {(file.size_bytes / 1024).toFixed(0)} KB
          </span>
          {file.column_count != null && (
            <span className="text-[11px] text-[var(--muted-foreground)]">
              {file.column_count} cols
            </span>
          )}
          {file.estimated_row_count != null && (
            <span className="text-[11px] text-[var(--muted-foreground)]">
              {file.estimated_row_count.toLocaleString()} rows
            </span>
          )}
          {file.logical_group !== "_root" && (
            <span className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded text-[var(--muted-foreground)]">
              {file.logical_group}
            </span>
          )}
        </div>
      </div>
      {/* No badge for "processable" or "encoding_ambiguous" — both are
          included/selected by default and behave identically from here on;
          repeating an "encoding?" flag on every row when the top-level
          narrative banner already covers it is exactly the per-row noise
          this was decluttered to remove. Only statuses that actually
          exclude a file get a visible reason. */}
      {!isProcessable && (
        <span
          className={cn(
            "text-[11px] capitalize",
            statusColors[file.status] || "text-[var(--muted-foreground)]",
          )}
        >
          {file.status}
        </span>
      )}
    </label>
  );
}
